import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { categoriasConfig, getEtiquetasPorCategoria } from '../config/categoriasConfig';

const ConcursoConfiguracion = ({
  categoriasSeleccionadas,
  etiquetasSeleccionadas,
  categoriasExpandidas,
  cantidadPreguntas,
  duracionConcurso,
  preguntasPorDificultad,
  distribucionAutomatica,
  setCategoriasSeleccionadas,
  setEtiquetasSeleccionadas,
  setCategoriasExpandidas,
  setCantidadPreguntas,
  setDuracionConcurso,
  setPreguntasPorDificultad,
  setDistribucionAutomatica,
  handleCategoriaChange,
  handleEtiquetaChange,
  iniciarConcurso,
  obtenerTodasLasEtiquetasSeleccionables
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 dark:bg-gray-800 dark:text-white transition-colors duration-300">
      <h3 className="text-lg sm:text-xl font-semibold mb-4 dark:text-white">Configuración del Concurso</h3>
      
      {/* Selección de categorías */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 dark:text-white gap-2">
          <h4 className="text-base sm:text-lg font-medium dark:text-white">Seleccionar Categorías</h4>
          <button
            type="button"
            onClick={() => {
              if (categoriasSeleccionadas.length === Object.keys(categoriasConfig).length) {
                setCategoriasSeleccionadas([]);
                setEtiquetasSeleccionadas([]);
              } else {
                setCategoriasSeleccionadas(Object.keys(categoriasConfig));
              }
            }}
            className="text-xs sm:text-sm bg-blue-500 text-white py-1 px-2 sm:px-3 rounded hover:bg-blue-600 transition-colors dark:bg-blue-700 dark:hover:bg-blue-800 duration-300"
          >
            {categoriasSeleccionadas.length === Object.keys(categoriasConfig).length ? 'Deseleccionar todas' : 'Seleccionar todas'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 sm:gap-3 text-[11px] sm:text-sm">
          {Object.keys(categoriasConfig).map(categoria => (
            <label key={categoria} className="flex items-center dark:text-white text-[11px] sm:text-sm md:text-base leading-tight">
                    <span className="mr-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={categoriasSeleccionadas.includes(categoria)}
                          onChange={() => handleCategoriaChange(categoria)}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 transition-colors"></div>
                        <div className="absolute left-1 top-1 bg-white border border-gray-300 dark:border-gray-600 rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform peer-checked:translate-x-4 sm:peer-checked:translate-x-5"></div>
                      </label>
                    </span>
              {categoriasConfig[categoria].nombre}
            </label>
          ))}
        </div>
      </div>

      {/* Selección de etiquetas */}
      {categoriasSeleccionadas.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 dark:text-white gap-2">
            <h4 className="text-base sm:text-lg font-medium dark:text-white">Seleccionar Etiquetas</h4>
            <button
              type="button"
              onClick={() => {
                const todasLasEtiquetas = obtenerTodasLasEtiquetasSeleccionables();
                
                if (etiquetasSeleccionadas.length === todasLasEtiquetas.length) {
                  setEtiquetasSeleccionadas([]);
                } else {
                  setEtiquetasSeleccionadas(todasLasEtiquetas);
                }
              }}
              className="text-xs sm:text-sm bg-blue-500 text-white py-1 px-2 sm:px-3 rounded hover:bg-blue-600 transition-colors dark:bg-blue-700 dark:hover:bg-blue-800 duration-300"
            >
              {etiquetasSeleccionadas.length === obtenerTodasLasEtiquetasSeleccionables().length ? 'Deseleccionar todas' : 'Seleccionar todas'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {categoriasSeleccionadas.map(categoria => {
              const etiquetasCategoria = getEtiquetasPorCategoria(categoria);
              const esCategoriaExpandida = categoriasExpandidas[categoria] || false;
              return (
                <div key={categoria} className={esCategoriaExpandida ? "w-full" : "inline-block mr-4"}>
                  <div className="flex items-center dark:text-white text-xs sm:text-sm">
                    <button
                      type="button"
                      onClick={() => setCategoriasExpandidas(prev => ({
                        ...prev,
                        [categoria]: !prev[categoria]
                      }))}
                      className="flex items-center text-xs sm:text-sm bg-gray-200 hover:bg-gray-300 py-1 px-2 rounded dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors duration-300 mr-2"
                    >
                      <span className={`transform transition-transform ${esCategoriaExpandida ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                      <span className="ml-2">{categoriasConfig[categoria].nombre}</span>
                    </button>
                  </div>
                  <AnimatePresence initial={false}>
                    {esCategoriaExpandida && (
                      <motion.div
                        key="acordeon"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden ml-6 mt-2"
                      >
                        {/* Slider de todas las etiquetas de la categoría */}
                        <div className="flex items-center mb-2">
                          <span className="mr-2 font-semibold text-xs sm:text-sm">Todas</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={etiquetasCategoria.every(etiqueta => etiquetasSeleccionadas.includes(etiqueta))}
                              ref={el => {
                                if (el) {
                                  el.indeterminate = etiquetasCategoria.some(etiqueta => etiquetasSeleccionadas.includes(etiqueta)) && !etiquetasCategoria.every(etiqueta => etiquetasSeleccionadas.includes(etiqueta));
                                }
                              }}
                              onChange={() => {
                                const todasMarcadas = etiquetasCategoria.every(etiqueta => etiquetasSeleccionadas.includes(etiqueta));
                                if (todasMarcadas) {
                                  setEtiquetasSeleccionadas(etiquetasSeleccionadas.filter(e => !etiquetasCategoria.includes(e)));
                                } else {
                                  setEtiquetasSeleccionadas([
                                    ...etiquetasSeleccionadas.filter(e => !etiquetasCategoria.includes(e)),
                                    ...etiquetasCategoria.filter(e => !etiquetasSeleccionadas.includes(e))
                                  ]);
                                }
                              }}
                              className="sr-only peer"
                            />
                            <motion.div layout className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 transition-colors"></motion.div>
                            <motion.div layout className="absolute left-1 top-1 bg-white border border-gray-300 dark:border-gray-600 rounded-full h-4 w-4 transition-transform peer-checked:translate-x-5"></motion.div>
                          </label>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 sm:gap-3 text-[11px] sm:text-sm">
                          {etiquetasCategoria.map(etiqueta => (
                            <label key={etiqueta} className="flex items-center dark:text-white text-[11px] sm:text-sm leading-tight min-w-0">
                              <span className="mr-2">
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={etiquetasSeleccionadas.includes(etiqueta)}
                                    onChange={() => handleEtiquetaChange(etiqueta)}
                                    className="sr-only peer"
                                  />
                                  <motion.div layout className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 transition-colors"></motion.div>
                                  <motion.div layout className="absolute left-1 top-1 bg-white border border-gray-300 dark:border-gray-600 rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform peer-checked:translate-x-4 sm:peer-checked:translate-x-5"></motion.div>
                                </label>
                              </span>
                              <span className="truncate">{etiqueta}</span>
                            </label>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Personalización de opciones */}
      <div className="mb-6">
        <h4 className="text-lg font-medium mb-3 dark:text-white">Opciones del Concurso</h4>
        
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Cantidad de Preguntas</label>
            <input
              type="number"
              value={cantidadPreguntas}
              onChange={(e) => {
                let value = parseInt(e.target.value);
                if (isNaN(value)) {
                  value = 12; // Valor por defecto si no es un número
                }
                setCantidadPreguntas(value);
              }}
              className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 text-sm"
            />
            {(cantidadPreguntas < 12 || cantidadPreguntas > 50) && (
              <span className="text-red-500 text-xs mt-1 block">El número de preguntas debe estar entre 12 y 50</span>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Duración (segundos)</label>
            <input
              type="number"
              min="0"
              max="3600"
              value={duracionConcurso}
              onChange={(e) => {
                const valor = Number(e.target.value);
                if (!Number.isFinite(valor) || valor <= 0) {
                  setDuracionConcurso(0);
                  return;
                }

                setDuracionConcurso(Math.min(3600, Math.floor(valor)));
              }}
              className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 text-sm"
            />
            <p className="text-[11px] sm:text-sm text-gray-500 mt-1 dark:text-gray-400 leading-tight">Se aplica a cada pregunta. 0 significa sin límite de tiempo.</p>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Preguntas por Dificultad</label>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-[11px] sm:text-sm">
            <div>
              <span className="block text-[11px] sm:text-sm text-gray-600 dark:text-gray-300">Fácil</span>
              <input
                type="number"
                min="0"
                max="20"
                value={preguntasPorDificultad.facil}
                onChange={(e) => setPreguntasPorDificultad({
                  ...preguntasPorDificultad,
                  facil: parseInt(e.target.value)
                })}
                className="w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 text-sm"
              />
            </div>
            <div>
              <span className="block text-[11px] sm:text-sm text-gray-600 dark:text-gray-300">Medio</span>
              <input
                type="number"
                min="0"
                max="20"
                value={preguntasPorDificultad.medio}
                onChange={(e) => setPreguntasPorDificultad({
                  ...preguntasPorDificultad,
                  medio: parseInt(e.target.value)
                })}
                className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 text-sm"
              />
            </div>
            <div>
              <span className="block text-[11px] sm:text-sm text-gray-600 dark:text-gray-300">Difícil</span>
              <input
                type="number"
                min="0"
                max="20"
                value={preguntasPorDificultad.dificil}
                onChange={(e) => setPreguntasPorDificultad({
                  ...preguntasPorDificultad,
                  dificil: parseInt(e.target.value)
                })}
                className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Botón para iniciar concurso */}
      <button
        onClick={iniciarConcurso}
        disabled={categoriasSeleccionadas.length === 0 && etiquetasSeleccionadas.length === 0 || cantidadPreguntas < 12 || cantidadPreguntas > 50}
        className="w-full bg-green-600 text-white py-3 px-6 rounded hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800 duration-300 text-sm sm:text-base"
      >
        Iniciar Concurso
      </button>
    </div>
  );
};

export default ConcursoConfiguracion;
